import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_BASE_URL, ADMIN_LOGIN_ENDPOINT, ADMIN_VERIFY_OTP_ENDPOINT, ADMIN_RESEND_OTP_ENDPOINT, ADMIN_LOGOUT_ENDPOINT, ADMIN_INFO_ENDPOINT, getAuthToken, setAuthToken, removeAuthToken, getAxiosConfig } from '../config/api';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getAuthToken();
    if (token) {
      try {
        // Verify token by fetching admin info
        const response = await axios.post(
          `${API_BASE_URL}/api${ADMIN_INFO_ENDPOINT}`,
          {},
          getAxiosConfig()
        );
        // The API returns the user object directly, not wrapped in data
        const adminData = response.data || response;
        if (adminData && adminData.type === 'admin') {
          setUser(adminData);
          setIsAuthenticated(true);
        } else {
          removeAuthToken();
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        removeAuthToken();
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const login = async (email, password, turnstileToken = '') => {
    try {
        const response = await axios.post(
          `${API_BASE_URL}/api${ADMIN_LOGIN_ENDPOINT}`,
          { email, password, turnstile_token: turnstileToken },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            withCredentials: true,
          }
        );

      // OTP required — credentials valid but need 2FA
      if (response.data && response.data.otp_required) {
        return {
          success: false,
          otpRequired: true,
          otpToken: response.data.otp_token,
          message: response.data.message,
          devOtpCode: response.data.dev_otp_code,
        };
      }

      if (response.data && response.data.access_token) {
        const token = response.data.access_token;
        setAuthToken(token);

        // Fetch admin info
        const adminResponse = await axios.post(
          `${API_BASE_URL}/api${ADMIN_INFO_ENDPOINT}`,
          {},
          getAxiosConfig()
        );

        // The API returns the user object directly, not wrapped in data
        const adminData = adminResponse.data || adminResponse;
        if (adminData && adminData.type === 'admin') {
          setUser(adminData);
          setIsAuthenticated(true);
          return { success: true };
        } else {
          removeAuthToken();
          return { success: false, error: 'Not an admin user' };
        }
      } else {
        return { success: false, error: 'Invalid response from server' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const data = error.response?.data;
      const err = data?.error;
      // 422 = validation failed (Laravel returns error as object: { email: [...], password: [...] })
      let errorMessage = 'Login failed. Please check your credentials.';
      if (err) {
        if (typeof err === 'string') {
          errorMessage = err;
        } else if (typeof err === 'object' && err !== null) {
          const first = Object.values(err).flat().find(Boolean);
          errorMessage = first || errorMessage;
        }
      }
      return { success: false, error: errorMessage };
    }
  };

  const verifyOtp = async (otpToken, otpCode) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api${ADMIN_VERIFY_OTP_ENDPOINT}`,
        { otp_token: otpToken, otp_code: otpCode },
        {
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          withCredentials: true,
        }
      );

      if (response.data && response.data.access_token) {
        const token = response.data.access_token;
        setAuthToken(token);

        const adminResponse = await axios.post(
          `${API_BASE_URL}/api${ADMIN_INFO_ENDPOINT}`,
          {},
          getAxiosConfig()
        );

        const adminData = adminResponse.data || adminResponse;
        if (adminData && adminData.type === 'admin') {
          setUser(adminData);
          setIsAuthenticated(true);
          return { success: true };
        } else {
          removeAuthToken();
          return { success: false, error: 'Not an admin user' };
        }
      }
      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      const data = error.response?.data;
      return { success: false, error: data?.error || 'Verification failed. Please try again.' };
    }
  };

  const resendOtp = async (otpToken) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api${ADMIN_RESEND_OTP_ENDPOINT}`,
        { otp_token: otpToken },
        {
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          withCredentials: true,
        }
      );

      if (response.data && response.data.otp_token) {
        return { success: true, otpToken: response.data.otp_token, message: response.data.message };
      }
      return { success: false, error: 'Failed to resend code.' };
    } catch (error) {
      const data = error.response?.data;
      return { success: false, error: data?.error || 'Failed to resend code.' };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint if needed
        await axios.post(
          `${API_BASE_URL}/api${ADMIN_LOGOUT_ENDPOINT}`,
          {},
          getAxiosConfig()
        );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeAuthToken();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    verifyOtp,
    resendOtp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

